import React, { useState, useEffect, useRef } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
  Container,
  Row,
  Col,
  Modal,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";

import BeatLoader from "react-spinners/BeatLoader";
import { useSnackbar } from "../../../utils/snackbar";

import ReactQuill from "react-quill";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "react-quill/dist/quill.snow.css";
import FormInput from "../../../components/FormInput";

// validations
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";

import {
  createEmailTemplate,
  getEmailTemplate,
  getAllEmailTemplates,
  updateEmailTemplate,
  deleteEmailTemplate,
} from "../../../api";

import LanguageSwitcher from "../../../components/LanguageSelector";
import { useTranslation } from "react-i18next";

interface TemplateType {
  id: string;
  email_template_title: string;
  subject: string | null;
  cc_recipients: string | null;
  message: string | null;
}

const EmailTemplate = () => {
  const modules = {
    toolbar: {
      container: "#toolbar", // Use the custom toolbar
    },
  };

  const { t, i18n } = useTranslation();

  const showSnackbar = useSnackbar();

  const [loading, setLoading] = useState(false);

  // Reference to ReactQuill editor
  const quillRef = useRef<ReactQuill | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // state variables
  const [filterText, setFilterText] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);

  const [selectedValue, setSelectedValue] = useState<string>("");
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const [subject, setSubject] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");
  const [selectedValueOnTextArea, setValueOnTextArea] = useState("");

  const [emailTemplates, setEmailTemplates] = useState<TemplateType[]>([]);
  const [originalEmailTemplates, setOriginalEmailTemplates] = useState<
    TemplateType[]
  >([]);

  const [templateDeleteFlag, setTemplateDeleteFlag] = useState<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState("");

  const placeholders = [
    { label: "customer_full_name", value: "{{$customer_full_name}}" },
    { label: "customer_company_name", value: "{{$customer_company_name}}" },
    { label: "issue_date", value: "{{$issue_date}}" },
    { label: "due_date", value: "{{$due_date}}" },
    { label: "invoice_number", value: "{{$invoice_number}}" },
    { label: "today_date", value: "{{$today_date}}" },
  ];

  const defaultValues: any = {
    email_template_title: "",
    cc_recipients: "",
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response: any = await getAllEmailTemplates();

        setEmailTemplates(response.data);
        setOriginalEmailTemplates(response.data);
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [templateDeleteFlag]);

  const handleDeleteEmailTemplate = async (id: string) => {
    try {
      const payload = {
        templateId: id,
      };
      const response: any = await deleteEmailTemplate(payload);
      setTemplateDeleteFlag(new Date());
      showSnackbar(response.message, "info");
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const handleEditEmailTemplate = async (id: string) => {
    setSelectedEmailTemplateId(id);
    try {
      setIsEdit(true);

      const payload = {
        templateId: id,
      };

      const response = await getEmailTemplate(payload);
      const templateData = response.data;

      reset({
        email_template_title: templateData?.email_template_title,
        cc_recipients: templateData?.cc_recipients,
      });
      setSubject(templateData.subject);
      setEmailTemplate(templateData.message);
      setShowModal(true);
      setIsOpen(true);
    } catch (error: any) {
      console.log(error);
    }
  };

  // Define columns with the correct type
  const columns: TableColumn<(typeof emailTemplates)[0]>[] = [
    {
      name: t("Title"),
      selector: (row: TemplateType) => row.email_template_title ?? "",
      sortable: true,
      width: "250px",
    },
    {
      name: t("Subject"),
      selector: (row: TemplateType) => row.subject ?? "",
      sortable: true,
    },
    {
      name: t("CC Recipients"),
      selector: (row: TemplateType) => row.cc_recipients ?? "",
      sortable: true,
    },

    {
      name: t("Actions"),
      width: "100px",
      cell: row => (
        <>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleEditEmailTemplate(row.id)}
          >
            <i className="bi bi-pencil"></i>
          </Button>

          <Button
            variant="warning"
            size="sm"
            onClick={() => handleDeleteEmailTemplate(row.id)}
            className="ms-3"
          >
            <i className="bi bi-trash"></i>
          </Button>
        </>
      ),
    },
  ];

  const resolver = yupResolver(
    yup.object().shape({
      email_template_title: yup
        .string()
        .required("Please Enter Template Title"),
    }),
  );

  const methods = useForm({ defaultValues, resolver });
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = methods;

  // Insert selected placeholder at cursor position
  const insertOnQuill = (value: string) => {
    if (!quillRef.current) return;

    const editor = quillRef.current.editor;
    if (!editor) return;

    // ✅ Force focus on the editor before inserting text
    editor.focus();

    const range = editor.getSelection();
    const index = range ? range.index : editor.getLength(); // If no selection, insert at end

    editor.insertText(index, value);
  };

  const insertOnTextArea = (value: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;

    const beforeText = subject.substring(0, startPos);
    const afterText = subject.substring(endPos);

    const newText = beforeText + value + afterText;
    setSubject(newText);

    // // ✅ Restore cursor position after insertion
    // setTimeout(() => {
    //   textarea.selectionStart = textarea.selectionEnd = startPos + value.length;
    //   textarea.focus();
    // }, 0);
  };
  //component functions
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedValue(event.target.value);
    console.log("Selected Value:", event.target.value);
  };

  const handleClose = (): void => setShowModal(false);
  const handleShow = (): void => {
    setSubject("");
    setEmailTemplate("");
    reset({
      email_template_title: "",
      cc_recipients: "",
    });
    setShowModal(true);
    setIsEdit(false);
    setIsOpen(true);
  };

  // Function to toggle visibility
  const handleToggle = () => {
    setIsVisible(!isVisible);
  };

  const handleFilterTable = (inputValue: string) => {
    const filteredData = emailTemplates.filter(item =>
      item.email_template_title
        .toLowerCase()
        .includes(inputValue.toLowerCase()),
    );
    setFilterText(inputValue);
    setEmailTemplates(filteredData);

    if (inputValue == "") {
      setEmailTemplates(originalEmailTemplates);
    }
  };

  const handleCustomChange = (value: string) => {
    console.log("Selected option:", value); // Perform your logic here
  };

  const onSubmitForm = async (values: any) => {
    setLoading(true);
    if (!isEdit) {
      const payload = {
        ...values,

        subject: subject,
        message: emailTemplate,
      };

      try {
        const response = await createEmailTemplate(payload);
        setShowModal(false);
        setTemplateDeleteFlag(new Date());
        setSubject("");
        setEmailTemplate("");
        reset({
          email_template_title: "",
          cc_recipients: "",
        });

        setLoading(false);
      } catch (error: any) {
        setLoading(false);
      }
    } else {
      const payload = {
        ...values,
        templateId: selectedEmailTemplateId,

        subject: subject,
        message: emailTemplate,
      };

      try {
        const res = await updateEmailTemplate(payload);
        setShowModal(false);
        setTemplateDeleteFlag(new Date());
        setSubject("");
        setEmailTemplate("");
        reset({
          email_template_title: "",
          cc_recipients: "",
        });
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
      }
    }
  };

  // Custom styles for the table
  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "rgb(17, 125, 192)",
        color: "white",
        fontWeight: "500 !important",
        fontSize: ".975rem !important",
        // fontFamily: "Inter, sans-serif !important",
        letterSpacing: "0.5px",
      },
    },
    cells: {
      style: {
        padding: "10px 16px",
        fontSize: ".975rem",
        fontWeight: "400",
        color: "rgb(17 39 68)",
      },
    },
    pagination: {
      style: {
        fontSize: "14px",
        marginTop: "10px",
        justifyContent: "center",
        display: "flex",
      },
    },
  };

  return (
    <>
      {/* Search Input */}
      <div
        className="d-flex justify-content-between flex-column flex-md-row gap-3 mt-4"
        style={{ marginBottom: "10px" }}
      >
        <div>
          <div className="input-group bg-white rounded-pill border">
            <span className="input-group-text bg-white border-0 rounded-pill">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              placeholder={t("Search...")}
              className="form-control border-0 rounded-pill"
              value={filterText}
              onChange={e => {
                handleFilterTable(e.target.value);
              }}
              style={{ outline: "none", boxShadow: "none" }}
            />
          </div>
        </div>

        <Button
          variant="secondary"
          style={{
            fontSize: "0.875rem",
            fontWeight: "500",
            whiteSpace: "nowrap",
          }}
          onClick={handleShow}
          disabled={loading}
        >
          + {t("Add Template")}
        </Button>
      </div>
      {/* DataTable */}
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "500px",
          }}
        >
          <BeatLoader color="gray" size={10} />
        </div>
      ) : (
        <DataTable
          title=""
          columns={columns}
          data={Array.isArray(emailTemplates) ? emailTemplates : []}
          pagination
          highlightOnHover
          striped
          fixedHeader
          persistTableHead
          sortIcon={
            <span
              style={{ color: "white", fontSize: "10px", marginLeft: "2px" }}
            >
              ▼
            </span>
          } // Always show sorting icon
          customStyles={customStyles} // Custom styles for header and pagination
          noDataComponent={
            // Display 'No results found' message when no data matches the search query
            <div
              style={{ textAlign: "center", padding: "20px", color: "gray" }}
            >
              {t("No results found")}
            </div>
          }
        />
      )}

      <Modal
        show={showModal}
        onHide={handleClose}
        size="lg"
        centered
        scrollable
      >
        <Form onSubmit={handleSubmit(onSubmitForm)}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "1.3rem" }}>
              {isEdit ? t("Update Template") : t("Add Template")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="modal-body-scrollable">
            <Row className="g-3 mb-3">
              <Col md="12">
                <FormInput
                  label={t("Email Template Title*")}
                  type="text"
                  name="email_template_title"
                  labelClassName="form-label"
                  placeholder={t("Enter title")}
                  className="form-control"
                  register={register}
                  errors={errors}
                  control={control}
                  style={{ boxShadow: "none", borderColor: "#ced4da" }}
                />
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col md="12">
                <FormInput
                  label={t("CC recipients")}
                  type="text"
                  name="cc_recipients"
                  labelClassName="form-label"
                  placeholder={t("Enter Recipients")}
                  className="form-control"
                  register={register}
                  errors={errors}
                  control={control}
                  style={{
                    boxShadow: "none",
                    borderColor: "#ced4da",
                  }}
                />
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col md="12">
                <Form.Group controlId="description">
                  <Form.Label>{t("Subject")} *</Form.Label>
                  <div
                    style={{
                      border: "1px solid rgb(206, 212, 218)",
                      overflow: "hidden",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#f5f7fa",
                        padding: "10px 20px",
                        borderBottom: "1px solid rgb(206, 212, 218)",
                      }}
                    >
                      <select
                        id="custom-select"
                        // className="ql-ignore"
                        onChange={e => {
                          if (e.target.value) {
                            insertOnTextArea(e.target.value);
                          }
                        }}
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                        }}
                      >
                        <option value="">{t("Custom Field")}</option>
                        {placeholders.map(field => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Form.Control
                      as="textarea"
                      ref={textareaRef}
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      rows={4}
                      style={{
                        boxShadow: "none",
                        borderColor: "#ced4da",
                        borderRadius: "0",
                        border: "none",
                      }}
                      placeholder={t("Enter your description...")}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col md="12">
                <Form.Group>
                  <Form.Label>{t("Message")} *</Form.Label>
                  <div
                    id="toolbar"
                    style={{ backgroundColor: "rgb(245, 247, 250)" }}
                  >
                    {/* Default toolbar elements */}
                    <select className="ql-header">
                      <option value="1">Heading 1</option>
                      <option value="2">Heading 2</option>
                      <option value="">Normal</option>
                    </select>
                    <button className="ql-bold"></button>
                    <button className="ql-italic"></button>
                    <button className="ql-underline"></button>
                    <button className="ql-list" value="ordered"></button>
                    <button className="ql-list" value="bullet"></button>

                    {/* Extra custom select box */}

                    <select
                      id="custom-select"
                      className="ql-ignore"
                      onChange={e => insertOnQuill(e.target.value)}
                      style={{
                        marginLeft: "10px",
                        border: "1px solid #ccc",
                        padding: "3px",
                        borderRadius: "5px",
                      }}
                    >
                      <option value="">{t("Custom Field")}</option>
                      {placeholders.map(field => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <ReactQuill
                    placeholder=""
                    style={{ height: "120px" }}
                    modules={modules}
                    value={emailTemplate}
                    onChange={setEmailTemplate}
                    ref={quillRef}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              {t("Close")}
            </Button>

            <Button variant="primary" type="submit" disabled={loading}>
              {isEdit ? t("Update Template") : t("Add Template")}
              {loading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <style>{`
        .modal-body-scrollable {
          height: 650px;
          overflow-y: auto;
        }
        @media (max-width: 768px) {
          .modal-body-scrollable {
            max-height: 400px;
          }
        }
        #custom-select {
           display: inline-block !important;
           color:  #212529 !important;
        }
      `}</style>
    </>
  );
};

export default EmailTemplate;
